import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Building2, CheckCircle2, Globe } from 'lucide-react';
import { Card } from '../components/ui/card';
import { API } from '../context/AuthContext';

const RELEVANCE = {
  navodaya: { ids: ['src_nvs', 'src_ncert', 'src_diksha'], label: 'ନବୋଦୟ (JNVST) ପାଇଁ ଅଫିସିଆଲ୍ ସୂତ୍ର — ପୂର୍ବ ପ୍ରଶ୍ନପତ୍ର, syllabus, ବିଜ୍ଞପ୍ତି' },
  board: { ids: ['src_bse_odisha', 'src_scert_odisha', 'src_ncert', 'src_diksha'], label: 'ଦଶମ ବୋର୍ଡ (BSE Odisha) ପାଇଁ ଅଫିସିଆଲ୍ ସୂତ୍ର — ମଡେଲ୍ ପେପର, ପୂର୍ବ ପ୍ରଶ୍ନପତ୍ର, syllabus' },
};

export default function SourcesPage() {
  const [params, setParams] = useSearchParams();
  const forKey = params.get('for');
  const relevance = RELEVANCE[forKey];
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/sources`).then(r => setSources(r.data)).catch(() => setError('Sources ଆଣିବାରେ ତ୍ରୁଟି — ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ')).finally(() => setLoading(false));
  }, []);

  const visible = relevance ? sources.filter(s => relevance.ids.includes(s.id)) : sources;
  const shown = visible.length ? visible : sources;

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium" data-testid="sources-back">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #0891b2 0%, #059669 60%, #14b8a6 100%)' }}>
        <Building2 size={26} />
        <h1 className="text-xl font-extrabold mt-2">🏛️ Official Education Source Hub</h1>
        <p className="text-xs text-white/90 mt-1">SCERT · BSE · NVS · NCERT · DIKSHA · OSEPA</p>
      </div>

      <Card className="rounded-2xl p-3 border-blue-200 bg-blue-50">
        <div className="flex items-start gap-2">
          <div className="text-lg">📖</div>
          <p className="text-[11px] text-blue-800 leading-relaxed">
            <strong>Read Only Policy:</strong> ଏହି sources ର content ଆମେ copy କରୁ ନାହିଁ। ପ୍ରତ୍ୟେକ button ଆପଣଙ୍କୁ ଅଫିସିଆଲ୍ website କୁ ନେଇଯିବ ଯେଉଁଠାରୁ ଆପଣ authorized content read/download କରି ପାରିବେ।
          </p>
        </div>
      </Card>

      {relevance && (
        <Card className="rounded-2xl p-3 border-emerald-200 bg-emerald-50 flex items-center justify-between gap-2" data-testid="sources-relevance">
          <p className="text-[11px] text-emerald-800 font-semibold leading-relaxed">{relevance.label}</p>
          <button onClick={() => setParams({})} className="text-[11px] font-bold text-emerald-700 underline shrink-0" data-testid="sources-show-all">ସବୁ ଦେଖନ୍ତୁ</button>
        </Card>
      )}

      {error && <Card className="rounded-2xl p-3 border-red-200 bg-red-50 text-xs text-red-700" data-testid="sources-error">{error}</Card>}
      {loading ? (
        <div className="text-center text-sm text-gray-500 py-8">Loading...</div>
      ) : shown.length === 0 ? (
        <Card className="rounded-2xl p-6 border-amber-200 bg-amber-50 text-center">
          <div className="text-2xl">🌐</div>
          <div className="font-bold text-amber-800 mt-2">No sources yet</div>
          <p className="text-xs text-amber-700 mt-1">Admin ଅଫିସିଆଲ୍ sources ଯୋଡ଼ିବା ପରେ ଏଠାରେ ଦେଖାଯିବ</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {shown.map(s => (
            <Card key={s.id} className="rounded-2xl p-4 border-gray-200" data-testid={`source-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shrink-0">
                  <Globe size={22} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-extrabold text-base text-gray-900">{s.organization}</div>
                      <div className="text-[11px] text-gray-500">{s.full_name}</div>
                    </div>
                    {s.verified && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1 shrink-0">
                        <CheckCircle2 size={10}/> Verified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 mt-2 leading-relaxed">{s.description}</p>

                  {/* Resource links */}
                  {(s.resources || []).length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {s.resources.map((r, i) => (
                        <a key={i} href={r.url} target="_blank" rel="noreferrer"
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-emerald-50 border border-gray-200 hover:border-emerald-300 transition-all">
                          <span className="text-xs font-semibold text-gray-800 truncate">📄 {r.title}</span>
                          <span className="text-[10px] text-emerald-600 flex items-center gap-1 shrink-0"><ExternalLink size={10}/> Read Online</span>
                        </a>
                      ))}
                    </div>
                  )}

                  <a href={s.url} target="_blank" rel="noreferrer" data-testid={`open-${s.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded-lg">
                    <ExternalLink size={12}/> Open Official Source
                  </a>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="text-center text-[10px] text-gray-500 pt-2">
        © Copyright of respective official sources. Gurusevakendra only indexes public URLs — does not host copyrighted content.
      </div>
    </div>
  );
}
